import axios from 'axios';
import api from './api';
import { getIdToken } from '../utils/firebase';
import { normalizeErrorPayload } from '../utils/apiError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Separate axios instance for admin checks that doesn't redirect on 401/403
// This prevents infinite refresh loops when checking admin status
const adminCheckApi = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to admin check requests
adminCheckApi.interceptors.request.use(
  async (config) => {
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Same renderable-`detail` guarantee as api.ts. This instance deliberately does
// NOT redirect on 401/403 (it would loop on the admin check), so it needs its
// own response interceptor to get the normalisation.
adminCheckApi.interceptors.response.use(
  (response) => response,
  (error) => {
    normalizeErrorPayload(error?.response?.data);
    return Promise.reject(error);
  }
);

// Types for analytics data
export interface TopEndpoint {
  endpoint: string;
  count: number;
}

export interface TopSearchType {
  type: string;
  count: number;
}

export interface TopFeature {
  feature: string;
  count: number;
}

export interface AnalyticsOverview {
  total_requests_today: number;
  total_requests_week: number;
  total_users: number;
  active_users_today: number;
  active_users_week: number;
  total_searches_today: number;
  avg_response_time_ms: number;
  error_rate_percent: number;
  top_endpoints: TopEndpoint[];
  top_search_types: TopSearchType[];
  top_features: TopFeature[];
}

export interface RequestLog {
  id: number;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  endpoint: string;
  method: string;
  status_code: number;
  duration_ms: number;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface SearchLog {
  id: number;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  search_type: string;
  query: string;
  filters: Record<string, unknown> | null;
  results_count: number | null;
  duration_ms: number | null;
  created_at: string;
}

export interface FeatureUsageStats {
  feature: string;
  total_usage: number;
  unique_users: number;
}

export interface UserActivity {
  user_id: string;
  email: string;
  display_name: string | null;
  subscription_tier: string;
  is_admin: boolean;
  total_requests: number;
  total_searches: number;
  /** Real sign-in (Firebase auth_time). null = none recorded since 2026-10-12. */
  last_login_at: string | null;
  /** Last authenticated request, 5-minute grain. */
  last_seen_at: string | null;
  created_at: string;
}

export interface AdminHealthCheck {
  status: string;
  admin: string;
  is_admin_flag: boolean;
}

// Request parameters
export interface RequestLogParams {
  start_date?: string;
  end_date?: string;
  endpoint?: string;
  method?: string;
  status_code?: number;
  user_id?: string;
  limit?: number;
  offset?: number;
}

export interface SearchLogParams {
  start_date?: string;
  end_date?: string;
  search_type?: string;
  user_id?: string;
  limit?: number;
  offset?: number;
}

// === Cost-tracking types ===
export type CostPeriod = '7d' | '30d' | '90d' | 'month_to_date' | string;

export interface CostSummary {
  period_start: string;
  period_end: string;
  total_calls: number;
  total_inr: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cached_input_tokens: number;
  total_tokens: number;
  distinct_users: number;
  distinct_traces: number;
  avg_inr_per_user: number;
}

export interface CostByFeature {
  feature: string;
  calls: number;
  distinct_traces: number;
  total_inr: number;
  total_tokens: number;
  avg_inr_per_trace: number;
}

export type Segment = 'spiking' | 'power' | 'heavy_feature' | 'casual' | 'dormant';

export interface CostByUser {
  user_id: string | null;
  user_email: string | null;
  calls: number;
  total_inr: number;
  total_tokens: number;
  top_feature: string | null;
  segment: Segment | null;
}

export interface SegmentBucket {
  segment: Segment;
  count: number;
}

export interface SegmentsOverview {
  period_start: string;
  period_end: string;
  buckets: SegmentBucket[];
  user_segments: Record<string, Segment>;  // user_id -> segment
}

export interface UserFeatureRow {
  feature: string;
  calls: number;
  total_inr: number;
  avg_inr_per_call: number;
}

export interface UserMatterRow {
  matter_id: number;
  matter_title: string | null;
  client_id: string | null;
  calls: number;
  total_inr: number;
  last_activity: string;
}

export interface UserDailyBucket {
  bucket_start: string;
  calls: number;
  total_inr: number;
}

export interface UserTopTrace {
  trace_id: string;
  feature: string;
  started_at: string;
  calls_in_trace: number;
  total_tokens: number;
  total_inr: number;
}

// === Signup funnel types ===

// Historical analytics type — keep 'persona' even though it's no longer a live
// signup step (removed 2026-07-28). The funnel renders past events, and users
// who onboarded before the change have real 'persona' step rows.
export type SignupStepName =
  | 'practice_area'
  | 'professional_identity'
  | 'firm_shape'
  | 'intent_and_source'
  | 'persona';

export interface SignupFunnelStepRow {
  step_name: SignupStepName;
  started: number;
  completed: number;
  skipped: number;
  abandoned: number;
  completion_rate: number;
  median_time_ms: number | null;
}

export interface SignupFunnelOverview {
  period_start: string;
  period_end: string;
  rows: SignupFunnelStepRow[];
  top_dropoff_step: SignupStepName | null;
  total_starters: number;
}

export interface SignupSegmentsBreakdown {
  period_start: string;
  period_end: string;
  by_firm_type: Array<[string, number]>;
  by_practice_area: Array<[string, number]>;
  by_acquisition_source: Array<[string, number]>;
  by_state: Array<[string, number]>;
}

export interface UserCostProfile {
  user_id: string;
  user_email: string | null;
  period_start: string;
  period_end: string;
  segment: Segment;
  total_inr: number;
  total_calls: number;
  total_tokens: number;
  distinct_traces: number;
  matter_count: number;
  top_feature: string | null;
  top_feature_share: number;
  daily: UserDailyBucket[];
  feature_mix: UserFeatureRow[];
  matters: UserMatterRow[];
  top_traces: UserTopTrace[];
}

export interface CostByModel {
  model: string;
  calls: number;
  total_inr: number;
  total_tokens: number;
}

export interface CostTimeseriesBucket {
  bucket_start: string;
  calls: number;
  total_inr: number;
  total_tokens: number;
}

export interface CostWasteRow {
  category: 'recitation_retries' | 'errors' | 'unattributed';
  feature: string | null;
  calls: number;
  total_inr: number;
  pct_of_total: number;
}

/**
 * Corporate-suite access state (GET/PUT /admin/corporate-access).
 *
 * TEMPORARY beta gate: declaring corporate practice records a `pending`
 * request; only an admin moves it to `granted`. Superseded by subscription
 * entitlements later.
 */
export type CorporateAccessStatus = 'none' | 'pending' | 'granted';

export interface CorporateAccessRow {
  user_id: string;
  email: string;
  display_name?: string | null;
  corporate_access: CorporateAccessStatus;
  practice_areas: string[];
  granted_by?: string | null;
  granted_at?: string | null;
}

/** A tier's entitlement caps (GET/PUT /admin/plan-limits). `max_matters: null`
 *  means unlimited. */
export interface PlanLimit {
  tier: string;
  max_matters: number | null;
  /** Bytes of advocate-uploaded documents; null = unlimited. */
  max_storage_bytes?: number | null;
  updated_by?: string | null;
}

// Admin API functions
export const adminApi = {
  // Check if current user has admin access
  // Uses separate axios instance to avoid redirect loop on 401/403
  checkAdminAccess: () => adminCheckApi.get<AdminHealthCheck>('/admin/analytics/health'),

  // Get dashboard overview
  getOverview: () => api.get<AnalyticsOverview>('/admin/analytics/overview'),

  // Get request logs with optional filters
  getRequestLogs: (params?: RequestLogParams) =>
    api.get<RequestLog[]>('/admin/analytics/requests', { params }),

  // Get search query logs with optional filters
  getSearchLogs: (params?: SearchLogParams) =>
    api.get<SearchLog[]>('/admin/analytics/searches', { params }),

  // Get feature usage statistics
  getFeatureUsage: (days?: number) =>
    api.get<FeatureUsageStats[]>('/admin/analytics/features', { params: { days } }),

  // Get user activity summary
  getUserActivity: (params?: { limit?: number; offset?: number }) =>
    api.get<UserActivity[]>('/admin/analytics/users', { params }),

  // === Cost dashboard methods ===
  getCostSummary: (period: CostPeriod = '7d') =>
    api.get<CostSummary>('/admin/analytics/costs', { params: { period } }),
  getCostByFeature: (period: CostPeriod = '7d') =>
    api.get<CostByFeature[]>('/admin/analytics/costs/by-feature', { params: { period } }),
  getCostByUser: (period: CostPeriod = '7d', limit = 50) =>
    api.get<CostByUser[]>('/admin/analytics/costs/by-user', { params: { period, limit } }),
  getCostByModel: (period: CostPeriod = '7d') =>
    api.get<CostByModel[]>('/admin/analytics/costs/by-model', { params: { period } }),
  getCostTimeseries: (period: CostPeriod = '7d', bucket: 'day' | 'hour' = 'day') =>
    api.get<CostTimeseriesBucket[]>('/admin/analytics/costs/timeseries', { params: { period, bucket } }),
  getCostWaste: (period: CostPeriod = '7d') =>
    api.get<CostWasteRow[]>('/admin/analytics/costs/waste', { params: { period } }),
  costExportUrl: (period: CostPeriod = '7d') => {
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    return `${BASE}/api/v1/admin/analytics/costs/export?period=${encodeURIComponent(period)}&format=csv`;
  },
  getCostSegments: (period: CostPeriod = '7d') =>
    api.get<SegmentsOverview>('/admin/analytics/costs/segments', { params: { period } }),
  getCostUserProfile: (userId: string, period: CostPeriod = '7d') =>
    api.get<UserCostProfile>(`/admin/analytics/costs/by-user/${userId}`, { params: { period } }),

  // === Signup funnel methods ===
  getSignupFunnel: (period: CostPeriod = '30d') =>
    api.get<SignupFunnelOverview>('/admin/analytics/signup/funnel', { params: { period } }),
  getSignupSegmentsBreakdown: (period: CostPeriod = '30d') =>
    api.get<SignupSegmentsBreakdown>('/admin/analytics/signup/segments', { params: { period } }),

  // === Plan limits (entitlements) ===
  getPlanLimits: () => api.get<PlanLimit[]>('/admin/plan-limits'),
  /** `maxStorageBytes` undefined = leave the storage cap as is; null = unlimited. */
  updatePlanLimit: (tier: string, maxMatters: number | null, maxStorageBytes?: number | null) =>
    api.put<PlanLimit>('/admin/plan-limits', {
      tier,
      max_matters: maxMatters,
      ...(maxStorageBytes === undefined ? {} : { max_storage_bytes: maxStorageBytes }),
    }),
  updateUserTier: (userId: string, tier: string) =>
    api.put<PlanLimit>(`/admin/users/${userId}/tier`, { tier }),

  // === Corporate access (temporary pre-subscription beta gate) ===
  /** Omit `status` for the queue (pending + granted); pass one to filter. */
  getCorporateAccess: (status?: CorporateAccessStatus) =>
    api.get<CorporateAccessRow[]>('/admin/corporate-access', {
      params: status ? { status } : undefined,
    }),
  updateCorporateAccess: (userId: string, status: CorporateAccessStatus) =>
    api.put<CorporateAccessRow>(`/admin/users/${userId}/corporate-access`, {
      status,
    }),
};

export default adminApi;
